import { useMemo, useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import {
  DashboardDataTable,
  DashboardDataTableSortDirection,
  safeStorage,
  useDashboardTableColumnPreferences,
  type DashboardDataTableColumnOption,
} from '@shigo-ui/dashboard-ui';
import { cn } from '@shigo-ui/ui';

import { useDebouncedValue } from '../../../../../../hooks/useDebouncedValue';
import { copyToClipboard } from '../../../../../../lib/clipboard';
import { downloadCsv } from '../../../../../../lib/downloadBlob';
import { dashboardT } from '../../../../../../lib/messages';
import { ShigoPermission } from '../../../../../auth/permissions';
import { useShigoPermissions } from '../../../../../auth/useShigoPermissions';
import { useIntegrationStatusCountsQuery, useIntegrationsQuery } from '../../../hooks';
import {
  integrationPageSizes,
  IntegrationSortDirection,
  isIntegrationSortProperty,
  type Integration,
  type IntegrationsFilter,
  type IntegrationSortProperty,
  type IntegrationStatus,
} from '../../../model';
import { createIntegrationsCsv } from '../../../views/integrationsCsv';
import {
  integrationsSelectionHeaderState,
  useIntegrationsSelection,
} from '../../../views/useIntegrationsSelection';
import { IntegrationsBulkBar } from '../../IntegrationsBulkBar';
import { IntegrationPlacementsDialog } from '../../IntegrationPlacementsDialog';
import { IntegrationsTableToolbar } from '../../IntegrationsTableToolbar';
import {
  INTEGRATION_COLUMN_IDS,
  INTEGRATION_DEFAULT_HIDDEN_COLUMN_IDS,
  INTEGRATION_LOCKED_COLUMN_IDS,
  IntegrationColumnId,
  integrationColumnLabel,
} from '../integrationTableColumnConfig';
import { createIntegrationsTableColumns } from '../integrationsTableColumns';

const columnPreferencesStorageKey = 'shigo.integrations.columns';
const lockedColumnIds = new Set<string>(INTEGRATION_LOCKED_COLUMN_IDS);
const embeddedHiddenColumnIds = new Set<string>([
  IntegrationColumnId.Srpm,
  IntegrationColumnId.BidRatio,
  IntegrationColumnId.Revenue7d,
  IntegrationColumnId.Ecpm7d,
]);
const columnOptions = INTEGRATION_COLUMN_IDS.map((id) => ({
  id,
  label: integrationColumnLabel(id),
  locked: lockedColumnIds.has(id),
})) satisfies DashboardDataTableColumnOption[];

export interface IntegrationsTableController {
  filter: IntegrationsFilter;
  setSearch: (value: string) => void;
  setStatuses: (value: IntegrationStatus[]) => void;
  setSort: (property: IntegrationSortProperty | null, direction: IntegrationSortDirection) => void;
  setPagination: (page: number, size: number) => void;
}

export function IntegrationsTableView(props: {
  controller: IntegrationsTableController;
  demandPartnerId?: string;
  embedded?: boolean;
}) {
  const navigate = useNavigate();
  const { can } = useShigoPermissions();
  const { filter } = props.controller;
  const query = useIntegrationsQuery(filter, props.demandPartnerId);
  const counts = useIntegrationStatusCountsQuery(filter.searchTerm, props.demandPartnerId);
  const search = useDebouncedValue(filter.searchTerm, props.controller.setSearch, 650);
  const canCreate = can(ShigoPermission.DemandIntegrationsCreate);
  const canEdit = can(ShigoPermission.DemandIntegrationsEdit);
  const canDuplicate = can(ShigoPermission.DemandIntegrationsDuplicate);
  const [placementsIntegration, setPlacementsIntegration] = useState<Integration | null>(null);
  const { hidden, setVisible, reset } = useDashboardTableColumnPreferences(
    columnPreferencesStorageKey,
    INTEGRATION_COLUMN_IDS,
    INTEGRATION_DEFAULT_HIDDEN_COLUMN_IDS,
    safeStorage(),
    INTEGRATION_LOCKED_COLUMN_IDS,
  );
  const showSelection = !props.embedded;
  const pageItems = query.data?.content ?? [];
  const pageIds = pageItems.map((integration) => integration.id);
  const selection = useIntegrationsSelection(
    [
      filter.searchTerm,
      filter.statuses.join(','),
      filter.sortProperty,
      filter.sortDirection,
      filter.page,
      filter.size,
    ].join('|'),
  );

  const columns = useMemo(() => {
    const selectionProps = showSelection
      ? {
          selected: selection.selected,
          onToggleRow: selection.toggle,
          headerChecked: integrationsSelectionHeaderState(selection.selected, pageIds),
          onHeaderChange: (checked: boolean) => {
            selection.toggleAll(pageIds, checked);
          },
        }
      : undefined;

    return createIntegrationsTableColumns({
      canEdit,
      canDuplicate,
      navigate,
      onShowPlacements: setPlacementsIntegration,
      showDemandPartner: !props.demandPartnerId,
      selection: selectionProps,
    }).filter(
      (column) =>
        !hidden.has(column.id) && !(props.embedded && embeddedHiddenColumnIds.has(column.id)),
    );
  }, [
    canDuplicate,
    canEdit,
    hidden,
    navigate,
    pageIds,
    props.demandPartnerId,
    props.embedded,
    selection,
    showSelection,
  ]);

  const handleExportCsv = () => {
    const selectedItems = pageItems.filter((integration) => selection.selected.has(integration.id));
    const csv = createIntegrationsCsv(
      columns.map((column) => column.id),
      selectedItems,
    );
    downloadCsv(csv, 'integrations-export.csv');
  };

  const handleCopyIds = () => {
    void copyToClipboard([...selection.selected].join(', '), {
      successMessage: dashboardT('integrations.idsCopied', { count: selection.selected.size }),
      errorMessage: dashboardT('integrations.idsCopyError'),
    });
  };

  const bulkBar =
    showSelection && selection.selected.size > 0 ? (
      <IntegrationsBulkBar
        selectedCount={selection.selected.size}
        onExportCsv={handleExportCsv}
        onCopyIds={handleCopyIds}
        onClear={selection.clear}
      />
    ) : undefined;

  return (
    <main
      className={cn('min-w-0', props.embedded ? 'flex-1 p-0' : 'page-gutter flex-1 py-4 md:py-6')}
    >
      <DashboardDataTable
        title={dashboardT('integrations.title')}
        tableLabel={dashboardT('integrations.title')}
        columns={columns}
        items={pageItems}
        getRowKey={(integration) => integration.id}
        loading={query.isLoading}
        loadingMessage={dashboardT('integrations.loading')}
        errorMessage={query.isError ? dashboardT('integrations.loadError') : undefined}
        emptyMessage={dashboardT('integrations.empty')}
        minWidthClassName="min-w-max table-auto"
        toolbar={
          <IntegrationsTableToolbar
            searchValue={search.draft}
            onSearchChange={search.setDraft}
            statusValue={filter.statuses}
            statusCounts={counts.data ?? {}}
            onStatusChange={props.controller.setStatuses}
            canCreate={canCreate}
            demandPartnerId={props.demandPartnerId}
            columnOptions={columnOptions.filter(
              ({ id }) =>
                !(props.embedded && embeddedHiddenColumnIds.has(id)) &&
                (id !== IntegrationColumnId.Actions || canEdit || canDuplicate),
            )}
            hiddenColumnIds={hidden}
            onToggleColumn={setVisible}
            onResetColumns={reset}
            bulkBar={bulkBar}
          />
        }
        pagination={{
          value: {
            page: filter.page,
            pageSize: filter.size,
            totalItems: query.data?.totalElements ?? 0,
          },
          pageSizes: integrationPageSizes,
          disabled: query.isFetching,
          onChange: (value) => {
            props.controller.setPagination(value.page, value.pageSize);
          },
        }}
        sort={{
          value: filter.sortProperty
            ? {
                key: filter.sortProperty,
                direction:
                  filter.sortDirection === IntegrationSortDirection.Asc
                    ? DashboardDataTableSortDirection.Asc
                    : DashboardDataTableSortDirection.Desc,
              }
            : undefined,
          onChange: (value) => {
            if (!value) {
              props.controller.setSort(null, filter.sortDirection);
              return;
            }
            if (!isIntegrationSortProperty(value.key)) return;
            props.controller.setSort(
              value.key,
              value.direction === DashboardDataTableSortDirection.Asc
                ? IntegrationSortDirection.Asc
                : IntegrationSortDirection.Desc,
            );
          },
        }}
      />
      {placementsIntegration ? (
        <IntegrationPlacementsDialog
          integration={placementsIntegration}
          onOpenChange={(open) => {
            if (!open) setPlacementsIntegration(null);
          }}
        />
      ) : null}
    </main>
  );
}
